// Boomin' — Profile edit screen (/me/edit)
//
// Redesigned replacement for the legacy /users/profile/edit page. Self-contained
// controlled form (no react-hook-form / heroicons / cropper) styled with the
// redesign tokens.

import React, { useCallback, useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useHistory } from "react-router-dom";
import {
  BRAND,
  BRAND_SOFT,
  PictX,
  AppHeader,
  AppFooter,
  useBoominFx,
} from "./shared";
import { DEFAULT_USER_IMAGE, MAX_IMAGE_SIZE } from "../../Constants";
import { convertBase64 } from "../../utils";
import {
  ProfileUpdate,
  fetchProfile,
  isUserLoggedIn,
  lookupPostalCode,
  updateProfile,
} from "./api";

const brand = BRAND;
const brandSoft = BRAND_SOFT;

type Form = ProfileUpdate & { email: string };

const EMPTY: Form = {
  user_name: "",
  first_name: "",
  last_name: "",
  email: "",
  phone: "",
  birth_date: "",
  gender: "",
  state: "",
  city: "",
  address_line_1: "",
  address_line_2: "",
  postal_code: "",
  profile_image: "",
};

type Errors = Partial<Record<keyof Form, string>>;

const KANA_KANJI = /^[ぁ-んァ-ヶー一-龠々〆ヵヶ]+$/;

const validate = (f: Form): Errors => {
  const e: Errors = {};
  if (!f.user_name.trim()) e.user_name = "ユーザーネームを入力してください";
  else if (f.user_name.length > 20) e.user_name = "ユーザーネームが長すぎます";
  if (!f.last_name.trim()) e.last_name = "姓を入力してください";
  if (!f.first_name.trim()) e.first_name = "名を入力してください";
  // The server requires gender / birth_date / postal_code as well.
  if (!f.gender) e.gender = "性別を選択してください";
  if (!f.birth_date) e.birth_date = "生年月日を入力してください";
  if (!f.phone.trim()) e.phone = "電話番号を入力してください";
  else if (!/^[0-9]+$/.test(f.phone)) e.phone = "電話番号は半角数字で入力してください";
  else if (f.phone.length < 8) e.phone = "電話番号が短すぎます";
  if (!f.postal_code.trim()) e.postal_code = "郵便番号を入力してください";
  else if (!/^[0-9]+$/.test(f.postal_code))
    e.postal_code = "郵便番号は半角数字で入力してください";
  if (!f.state.trim()) e.state = "都道府県を入力してください";
  else if (!KANA_KANJI.test(f.state)) e.state = "都道府県の形式が不正です";
  if (!f.city.trim()) e.city = "市区町村を入力してください";
  if (!f.address_line_1.trim()) e.address_line_1 = "番地を入力してください";
  return e;
};

export const EditProfileScreen: React.FC = () => {
  useBoominFx();
  const history = useHistory();

  const loggedIn = isUserLoggedIn();
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [form, setForm] = useState<Form>(EMPTY);
  const [errors, setErrors] = useState<Errors>({});
  const fileRef = useRef<HTMLInputElement>(null);
  const [pcLooking, setPcLooking] = useState<boolean>(false);

  const set = useCallback(
    <K extends keyof Form>(key: K, value: Form[K]) =>
      setForm((f) => ({ ...f, [key]: value })),
    []
  );

  const load = useCallback(async () => {
    if (!loggedIn) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const res = await fetchProfile();
      const u = res.user;
      const birth =
        !u.birth_date || u.birth_date.startsWith("0001-01-01")
          ? ""
          : u.birth_date.split("T")[0];
      setForm({
        user_name: u.user_name || "",
        first_name: u.first_name || "",
        last_name: u.last_name || "",
        email: u.email || "",
        phone: u.phone || "",
        birth_date: birth,
        gender: u.gender || "",
        state: u.state || "",
        city: u.city || "",
        address_line_1: u.address_line_1 || "",
        address_line_2: u.address_line_2 || "",
        postal_code: u.postal_code || "",
        // Legacy default images were hosted URLs; treat those as "no image".
        profile_image:
          u.profile_image && !u.profile_image.startsWith("http")
            ? u.profile_image
            : "",
      });
    } catch (e: any) {
      setError(typeof e === "string" ? e : e?.message || "読み込みに失敗しました");
    } finally {
      setLoading(false);
    }
  }, [loggedIn]);

  useEffect(() => {
    load();
  }, [load]);

  const onPickImage = async (ev: React.ChangeEvent<HTMLInputElement>) => {
    const file = ev.target.files?.[0];
    if (!file) return;
    if (file.size >= MAX_IMAGE_SIZE) {
      setError("画像のファイルサイズが大きすぎます");
      return;
    }
    try {
      const base64 = await convertBase64(file);
      set("profile_image", String(base64));
    } catch {
      setError("画像の読み込みに失敗しました");
    }
  };

  const onLookupPostal = async () => {
    if (!form.postal_code || pcLooking) return;
    setPcLooking(true);
    try {
      const addr = await lookupPostalCode(form.postal_code);
      if (addr) {
        setForm((f) => ({
          ...f,
          state: addr.state || f.state,
          city: addr.city || f.city,
          address_line_1: addr.addressLine1 || f.address_line_1,
        }));
      } else {
        setError("住所が見つかりませんでした");
      }
    } finally {
      setPcLooking(false);
    }
  };

  const onSubmit = async (ev: React.FormEvent) => {
    ev.preventDefault();
    if (saving) return;
    const v = validate(form);
    setErrors(v);
    if (Object.keys(v).length > 0) {
      setError("入力内容をご確認ください");
      return;
    }
    setError("");
    setSaving(true);
    try {
      const { email, ...rest } = form;
      void email; // not accepted by the update endpoint
      await updateProfile({
        ...rest,
        profile_image: rest.profile_image || DEFAULT_USER_IMAGE,
      });
      const nextPage = (() => {
        try {
          return localStorage.getItem("nextPage");
        } catch {
          return null;
        }
      })();
      if (nextPage) {
        try {
          localStorage.removeItem("nextPage");
        } catch {
          /* ignore */
        }
        history.push(nextPage);
      } else {
        history.push("/me");
      }
    } catch (e: any) {
      setError(typeof e === "string" ? e : e?.message || "更新に失敗しました");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>プロフィール編集 | Boomin'</title>
      </Helmet>
      <AppHeader />
      <div
        style={{
          background: "#FFFBF7",
          minHeight: "100vh",
          paddingBottom: 120,
          position: "relative",
        }}
      >
        <EditHeader onBack={() => history.push("/me")} />

        {!loggedIn ? (
          <SignInPrompt />
        ) : loading ? (
          <FormSkeleton />
        ) : (
          <form onSubmit={onSubmit}>
            {/* Avatar */}
            <div style={{ padding: "8px 20px 0", display: "flex", justifyContent: "center" }}>
              <div style={{ position: "relative" }}>
                <div
                  style={{
                    width: 96,
                    height: 96,
                    borderRadius: 999,
                    overflow: "hidden",
                    border: "2px solid #F0E6DC",
                    background: "#fff",
                  }}
                >
                  <img
                    src={form.profile_image || DEFAULT_USER_IMAGE}
                    alt="プロフィール画像"
                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                </div>
                <button
                  type="button"
                  aria-label="画像を変更"
                  onClick={() => fileRef.current?.click()}
                  style={{
                    position: "absolute",
                    right: -2,
                    bottom: -2,
                    width: 34,
                    height: 34,
                    borderRadius: 999,
                    border: "2px solid #FFFBF7",
                    background: brand,
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: `0 4px 12px ${brand}55`,
                  }}
                >
                  <PictX name="plus" size={18} stroke="#fff" strokeWidth={2.2} />
                </button>
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  onChange={onPickImage}
                  style={{ display: "none" }}
                />
              </div>
            </div>

            <Card title="基本情報">
              <Field label="ユーザーネーム" required error={errors.user_name}>
                <TextInput
                  value={form.user_name}
                  onChange={(v) => set("user_name", v)}
                  placeholder="例）Boomin'"
                  autoComplete="username"
                />
              </Field>
              <Row>
                <Field label="姓" required error={errors.last_name}>
                  <TextInput
                    value={form.last_name}
                    onChange={(v) => set("last_name", v)}
                    placeholder="例）田中"
                    autoComplete="family-name"
                  />
                </Field>
                <Field label="名" required error={errors.first_name}>
                  <TextInput
                    value={form.first_name}
                    onChange={(v) => set("first_name", v)}
                    placeholder="例）太郎"
                    autoComplete="given-name"
                  />
                </Field>
              </Row>
              <Field label="性別" required error={errors.gender}>
                <div style={{ display: "flex", gap: 10 }}>
                  {[
                    { id: "male", title: "男性" },
                    { id: "female", title: "女性" },
                  ].map((g) => {
                    const on = form.gender === g.id;
                    return (
                      <button
                        key={g.id}
                        type="button"
                        onClick={() => set("gender", g.id)}
                        style={{
                          flex: 1,
                          padding: "11px 0",
                          borderRadius: 12,
                          border: on ? `1.5px solid ${brand}` : "1px solid #E5DBD0",
                          background: on ? brandSoft : "#fff",
                          color: on ? brand : "#666",
                          fontWeight: 700,
                          fontSize: 14,
                          cursor: "pointer",
                          fontFamily: "inherit",
                        }}
                      >
                        {g.title}
                      </button>
                    );
                  })}
                </div>
              </Field>
              <Field label="生年月日" required error={errors.birth_date}>
                <TextInput
                  type="date"
                  value={form.birth_date}
                  onChange={(v) => set("birth_date", v)}
                />
              </Field>
            </Card>

            <Card title="連絡先">
              <Field label="メールアドレス" hint="変更できません">
                <TextInput value={form.email} onChange={() => {}} disabled />
              </Field>
              <Field label="電話番号" required error={errors.phone}>
                <TextInput
                  type="tel"
                  value={form.phone}
                  onChange={(v) => set("phone", v)}
                  placeholder="例）08012345678"
                  autoComplete="tel"
                />
              </Field>
            </Card>

            <Card title="お住まい">
              <Field label="郵便番号" required error={errors.postal_code}>
                <div style={{ display: "flex", gap: 8 }}>
                  <div style={{ flex: 1 }}>
                    <TextInput
                      value={form.postal_code}
                      onChange={(v) => set("postal_code", v)}
                      placeholder="例）0993225"
                      autoComplete="postal-code"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={onLookupPostal}
                    disabled={pcLooking || !form.postal_code}
                    style={{
                      flexShrink: 0,
                      padding: "0 16px",
                      borderRadius: 12,
                      border: 0,
                      background: pcLooking || !form.postal_code ? "#D6D0C6" : brand,
                      color: "#fff",
                      fontWeight: 700,
                      fontSize: 12,
                      cursor: pcLooking || !form.postal_code ? "default" : "pointer",
                      fontFamily: "inherit",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {pcLooking ? "検索中…" : "住所を自動入力"}
                  </button>
                </div>
              </Field>
              <Row>
                <Field label="都道府県" required error={errors.state}>
                  <TextInput
                    value={form.state}
                    onChange={(v) => set("state", v)}
                    placeholder="例）東京都"
                    autoComplete="address-level1"
                  />
                </Field>
                <Field label="市区町村" required error={errors.city}>
                  <TextInput
                    value={form.city}
                    onChange={(v) => set("city", v)}
                    placeholder="例）渋谷区"
                    autoComplete="address-level2"
                  />
                </Field>
              </Row>
              <Field label="番地" required error={errors.address_line_1}>
                <TextInput
                  value={form.address_line_1}
                  onChange={(v) => set("address_line_1", v)}
                  placeholder="例）恵比寿西2丁目20-17"
                />
              </Field>
              <Field label="建物名・部屋番号">
                <TextInput
                  value={form.address_line_2}
                  onChange={(v) => set("address_line_2", v)}
                  placeholder="例）202"
                />
              </Field>
            </Card>

            {error && (
              <div
                style={{
                  margin: "16px 20px 0",
                  padding: "12px 16px",
                  background: "#fff",
                  border: "1px solid #FFDAD0",
                  borderRadius: 14,
                  color: "#A6321E",
                  fontSize: 13,
                  lineHeight: 1.6,
                }}
              >
                {error}
              </div>
            )}

            <AppFooter />

            {/* Sticky save bar */}
            <div
              style={{
                position: "fixed",
                left: 0,
                right: 0,
                bottom: 0,
                padding: "14px 20px calc(env(safe-area-inset-bottom, 0px) + 16px)",
                background: "linear-gradient(to top, #FFFBF7 70%, rgba(255,251,247,0))",
                maxWidth: 640,
                margin: "0 auto",
                zIndex: 20,
              }}
            >
              <button
                type="submit"
                disabled={saving}
                style={{
                  width: "100%",
                  height: 56,
                  borderRadius: 999,
                  border: 0,
                  background: saving ? "#D6D0C6" : brand,
                  color: "#fff",
                  fontSize: 16,
                  fontWeight: 800,
                  cursor: saving ? "default" : "pointer",
                  boxShadow: saving ? "none" : `0 8px 20px ${brand}55`,
                  fontFamily: "inherit",
                }}
              >
                {saving ? "保存中…" : "更新する"}
              </button>
            </div>
          </form>
        )}
      </div>
    </>
  );
};

const EditHeader: React.FC<{ onBack: () => void }> = ({ onBack }) => (
  <div style={{ padding: "calc(env(safe-area-inset-top, 0px) + 24px) 20px 8px" }}>
    <button
      type="button"
      onClick={onBack}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        background: "transparent",
        border: 0,
        color: "#888",
        fontSize: 13,
        fontWeight: 700,
        cursor: "pointer",
        padding: 0,
        marginBottom: 8,
        fontFamily: "inherit",
      }}
    >
      <PictX name="chevronL" size={16} stroke="#888" strokeWidth={2} />
      マイページ
    </button>
    <h1
      style={{
        fontSize: 28,
        fontWeight: 800,
        margin: 0,
        letterSpacing: "0.01em",
        lineHeight: 1.15,
      }}
    >
      プロフィール編集<span style={{ color: brand }}>。</span>
    </h1>
  </div>
);

const Card: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div style={{ padding: "16px 20px 0" }}>
    <div
      style={{
        fontSize: 12,
        color: "#888",
        fontWeight: 800,
        letterSpacing: "0.08em",
        margin: "0 4px 8px",
      }}
    >
      {title}
    </div>
    <div
      style={{
        background: "#fff",
        borderRadius: 18,
        border: "1px solid #F0E6DC",
        padding: "16px",
        display: "flex",
        flexDirection: "column",
        gap: 14,
      }}
    >
      {children}
    </div>
  </div>
);

const Row: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div style={{ display: "flex", gap: 12 }}>{children}</div>
);

const Field: React.FC<{
  label: string;
  required?: boolean;
  hint?: string;
  error?: string;
  children: React.ReactNode;
}> = ({ label, required, hint, error, children }) => (
  <label style={{ display: "block", flex: 1, minWidth: 0 }}>
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 6,
        marginBottom: 6,
      }}
    >
      <span style={{ fontSize: 12, fontWeight: 700, color: "#555" }}>{label}</span>
      {required && (
        <span
          style={{
            fontSize: 9,
            fontWeight: 800,
            color: brand,
            border: `1px solid ${brand}`,
            borderRadius: 4,
            padding: "1px 4px",
          }}
        >
          必須
        </span>
      )}
      {hint && <span style={{ fontSize: 10, color: "#aaa" }}>{hint}</span>}
    </div>
    {children}
    {error && (
      <div style={{ marginTop: 4, fontSize: 11, color: "#D6452B" }}>{error}</div>
    )}
  </label>
);

const TextInput: React.FC<{
  value: string;
  onChange: (v: string) => void;
  type?: string;
  placeholder?: string;
  autoComplete?: string;
  disabled?: boolean;
}> = ({ value, onChange, type = "text", placeholder, autoComplete, disabled }) => (
  <input
    type={type}
    value={value}
    onChange={(e) => onChange(e.target.value)}
    placeholder={placeholder}
    autoComplete={autoComplete}
    disabled={disabled}
    style={{
      width: "100%",
      height: 44,
      padding: "0 14px",
      borderRadius: 12,
      border: "1px solid #E5DBD0",
      background: disabled ? "#F7F2EC" : "#fff",
      color: disabled ? "#999" : "#1A1A1A",
      fontSize: 15,
      fontFamily: "inherit",
      boxSizing: "border-box",
      outline: "none",
    }}
  />
);

const SignInPrompt: React.FC = () => (
  <div
    style={{
      margin: "20px 20px 0",
      padding: "28px 22px",
      background: "#fff",
      border: "1px solid #F0E6DC",
      borderRadius: 22,
      textAlign: "center",
      lineHeight: 1.7,
    }}
  >
    <div style={{ fontSize: 16, fontWeight: 800, color: "#1A1A1A", marginBottom: 6 }}>
      ログインが必要です
    </div>
    <div style={{ fontSize: 12, color: "#666", marginBottom: 18 }}>
      プロフィールを編集するにはログインしてください。
    </div>
    <a
      href="/public/oauth/instabase"
      style={{
        display: "inline-block",
        background: brand,
        color: "#fff",
        padding: "12px 28px",
        borderRadius: 999,
        fontSize: 14,
        fontWeight: 800,
        textDecoration: "none",
        boxShadow: `0 6px 18px ${brand}55`,
      }}
    >
      ログイン / 新規登録
    </a>
  </div>
);

const FormSkeleton: React.FC = () => (
  <div style={{ padding: "16px 20px 0", display: "flex", flexDirection: "column", gap: 16 }}>
    {[0, 1, 2].map((i) => (
      <div
        key={i}
        style={{
          height: i === 0 ? 180 : 140,
          borderRadius: 18,
          background: "#fff",
          border: "1px solid #F0E6DC",
        }}
      />
    ))}
  </div>
);

export default EditProfileScreen;
